import {
  Box,
  Button,
  Chip,
  Divider,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import strings from "global/constants/StringConstants";
import viewpaths from "global/constants/ViewPathConstants";
import {
  convertESTtoUserLocalDateAndTime,
  debounceEventHandler,
  initialPageFilterState,
  isTruthy,
  openErrorNotification,
  validatePageQuery,
  validatePerPageQuery,
} from "helpers/methods";
import CenteredAuthenticatedLayout from "layouts/CenteredAuthenticated/CenteredAuthenticatedLayout";
import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import ticketsStyles from "./Tickets.styles";
import SearchIcon from "assets/icons1/search_icon.svg";
import CenteredCardLayout from "layouts/CenteredTableView/CenteredCardLayout";
import CustomTable from "global/components/CustomTable/CustomTable";
import { tableHeaders } from "./TicketData";
import notifiers from "global/constants/NotificationConstants";
import {
  doFetchAllTicketsByStatus,
  doFetchAllTicketsCountByStatus,
  doFetchAllTicketsMetrics,
} from "./TicketServices";
import CreateTicket from "./CreateTicket/CreateTicket";
import { useTitle } from "utils/UseTitle";
import title from "global/constants/TitleConstants";
import { pageFilters } from "models/interfaces";

const Tickets = () => {
  useTitle(title.TICKETS);
  const theme = useTheme();
  const history = useHistory();
  const classes = ticketsStyles(theme);
  const getTab = new URLSearchParams(useLocation().search).get("tab");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [activeData, setActiveData] = useState<any[]>([]);
  const [count, setCount] = useState<number>();
  const [addCreateTicketModal, setAddCreateTicketModal] =
    useState<boolean>(false);
  const [metrics, setMetrics] = useState<any>();

  const searchTerm = useRef<any>(null);
  const urlParams = new URLSearchParams(useLocation().search);
  const pageFromUrl = validatePageQuery(urlParams.get("page"));
  const rowsPerPageFromUrl = validatePerPageQuery(urlParams.get("rowsPerPage"));
  const searchFromUrl = urlParams.get("search");

  const [ticketFilters, setTicketFilters] = useState<pageFilters>(
    initialPageFilterState(pageFromUrl, rowsPerPageFromUrl, searchFromUrl ?? "")
  );

  const tabConfig = [
    {
      label: "Open",
      value: "Open",
      key: "open",
      count: metrics && metrics["Open"],
    },
    {
      label: "In Process",
      value: "In Process",
      key: "in-process",
      count: metrics && metrics["InProcess"],
    },
    {
      label: "Complete",
      value: "Complete",
      key: "complete",
      count: metrics && metrics["Complete"],
    },
  ];

  const fetchTab = (
    selectedTab: string = "",
    searchBasedOn: "label" | "key"
  ) => {
    const newSelection = tabConfig.find(
      (tab) => tab[searchBasedOn] === selectedTab
    );
    return newSelection;
  };

  const tabToSelect = fetchTab(getTab ?? "", "key");
  const [currentTab, setCurrentTab] = useState<any>(
    tabToSelect?.label ?? "Open"
  );

  useEffect(() => {
    fetchMetrics();
  }, [currentTab]);

  useEffect(() => {
    const { page, rowsPerPage, searchText } = ticketFilters;
    if (searchTerm.current !== null) searchTerm.current.value = searchText;
    window.history.replaceState(
      null,
      "",
      `?page=${page}&rowsPerPage=${rowsPerPage}`
    );
    getTableData();
  }, [ticketFilters, currentTab]);

  const fetchMetrics = async () => {
    try {
      setIsLoading(true);
      const metrics = await doFetchAllTicketsMetrics();
      const metricsMap = metrics.reduce((map: any, metric: any) => {
        map[metric.name] = metric.count;
        return map;
      }, {});
      setMetrics(metricsMap);
    } catch (error: any) {
      setIsLoading(false);
      openErrorNotification(
        isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR
      );
    }
  };

  const getTableData = async () => {
    const { searchText, page, rowsPerPage } = ticketFilters;
    const tab = currentTab === "In Process" ? "InProcess" : currentTab;
    try {
      setIsLoading(true);
      const count = await doFetchAllTicketsCountByStatus(tab, searchText);
      const response = await doFetchAllTicketsByStatus(
        tab,
        rowsPerPage,
        page,
        searchText
      );
      const tableData = convertDataToTableFormat(response);
      setActiveData([...tableData]);
      setCount(count);
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      openErrorNotification(
        isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR
      );
    }
  };

  const getColors = {
    High: "rgb(245 101 101)",
    Medium: "rgb(246, 186, 95)",
    Low: "rgb(1, 202, 82)",
  } as { [key: string]: string };

  const convertDataToTableFormat = (data: any[]) => {
    return data.map((el: any) => {
      const assignees =
        el.hasOwnProperty("assigneeName") && el?.assigneeName.split(",");
      const displayedAssignees = assignees && assignees?.slice(0, 2).join(", ");
      const tooltipContent = displayedAssignees && assignees?.join(", ");
      return {
        id: el.id,
        name: (
          <Link
            color="#7551D0 !important"
            to={`${viewpaths.viewTicketsViewPath}/${el.id}?tab=${currentTab}`}
            style={{
              textDecoration: "none",
              color: "#7551D0 !important",
            }}
          >
            <Typography
              color="#7551D0 !important"
              variant="subtitle1"
              sx={{
                "&:hover": {
                  textDecoration: "underline",
                },
              }}
            >
              {el.name}
            </Typography>
          </Link>
        ),
        process: el.process,
        description: el.description,
        assigneeName: el.hasOwnProperty("assigneeName") ? (
          <Tooltip placement="top" title={tooltipContent} arrow>
            <Typography sx={{ wordBreak: "break-word", cursor: "pointer" }}>
              {assignees.length > 2
                ? `${displayedAssignees}, ...`
                : displayedAssignees}
            </Typography>
          </Tooltip>
        ) : (
          <Chip
            size="small"
            label="Unassigned"
            sx={{
              padding: 1,
            }}
          />
        ),
        activationDate: convertESTtoUserLocalDateAndTime(
          el.activationDate,
          "MMMM D, YYYY [at] h:mm A",
          "MM/DD/YYYY HH:mm:ss"
        ),
        priority: (
          <Typography
            sx={{
              color: getColors[el.priority],
              display: "flex",
              alignItems: "center",
            }}
            variant="subtitle2"
          >
            <Box
              sx={{
                borderRadius: "50%",
                height: 6,
                width: 6,
                marginRight: 1,
                backgroundColor: getColors[el.priority],
              }}
            />
            {el.priority}
          </Typography>
        ),
        createdByName: (
          <Stack direction={"column"} spacing={0}>
            <Typography variant="body1" color="text.primary">
              {el.createdByName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {convertESTtoUserLocalDateAndTime(
                el.activationDate,
                "MMMM D, YYYY [at] h:mm A",
                "MM/DD/YYYY HH:mm:ss"
              )}
            </Typography>
          </Stack>
        ),
        action: el.action,
      };
    });
  };

  const handleTabsChange = useCallback(
    (event: ChangeEvent<any>, value: string): void => {
      const selectedTab = fetchTab(value, "label");
      history.replace(`${viewpaths.ticketsViewPath}?tab=${selectedTab!.key}`);
      setCurrentTab(value);
      setTicketFilters({
        ...ticketFilters,
        rowsPerPage: 10,
        page: 1,
      });
    },
    []
  );

  const searchChangeHandler = (event: any) => {
    const searchText = event.target.value;
    setTicketFilters({
      ...ticketFilters,
      page: 1,
      searchText,
    });
  };

  const pageChangeHandler = (event: any, newPage: number) => {
    setTicketFilters({
      ...ticketFilters,
      page: newPage,
    });
  };

  const perPageRowsChangeHandler = (rowPerPage: number) => {
    setTicketFilters({
      ...ticketFilters,
      rowsPerPage: rowPerPage,
      page: 1,
    });
  };
  const getTicketPage = () => {
    return (
      <Stack direction="column" spacing={2}>
        <Stack>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h5" color="text.primary">
              Tickets
            </Typography>
            <Button
              id="ticket_add_btn"
              variant="contained"
              disabled={isLoading}
              onClick={() => setAddCreateTicketModal(true)}
            >
              Create Ticket
            </Button>
          </Stack>
          <Stack>
            <Tabs
              indicatorColor="primary"
              onChange={handleTabsChange}
              scrollButtons="auto"
              textColor="primary"
              value={currentTab}
              variant="scrollable"
            >
              {tabConfig.map((tab: any) => (
                <Tab
                  key={tab.value}
                  label={
                    <Stack direction={"row"} spacing={0.5}>
                      <Typography variant="subtitle1">{tab.label}</Typography>
                      <Typography variant="subtitle1">
                        {tab.count !== 0 && <Box> {tab.count} </Box>}
                      </Typography>
                    </Stack>
                  }
                  value={tab.value}
                  disabled={isLoading}
                />
              ))}
            </Tabs>
            <Divider />
          </Stack>
        </Stack>
        <CenteredCardLayout removeLastChildBottomPadding={true}>
          <Stack>
            <TextField
              id="tickets_search_field"
              placeholder="Search tickets"
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none !important",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  border: "none !important",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  border: "none !important",
                },
                "&.MuiOutlinedInput-root.Mui-focused": {
                  border: "none !important",
                },
              }}
              fullWidth
              onChange={debounceEventHandler(
                searchChangeHandler,
                strings.SEARCH_TIME_OUT
              )}
              inputRef={searchTerm}
              InputProps={{
                startAdornment: (
                  <Stack mr={1}>
                    <img src={SearchIcon} width="30px" height="20px" />
                  </Stack>
                ),
              }}
            />
          </Stack>
        </CenteredCardLayout>

        <CenteredCardLayout removeLastChildBottomPadding={true}>
          <Box>
            <CustomTable
              headers={tableHeaders}
              rows={activeData}
              page={ticketFilters.page}
              rowsPerPage={ticketFilters.rowsPerPage}
              onRowsPerPageChange={perPageRowsChangeHandler}
              onPageChange={pageChangeHandler}
              count={count}
              showJumpToPageNumber={false}
              handleOnChangePageInput={pageChangeHandler}
              showPagination={true}
              isLoading={isLoading}
            />
          </Box>
        </CenteredCardLayout>
      </Stack>
    );
  };

  return (
    <CenteredAuthenticatedLayout>
      {getTicketPage()}
      <CreateTicket
        openModel={addCreateTicketModal}
        setOpenModel={setAddCreateTicketModal}
        handleDialogClose={() => setAddCreateTicketModal(false)}
        getTableData={getTableData}
        fetchMetrics={fetchMetrics}
      />
    </CenteredAuthenticatedLayout>
  );
};

export default Tickets;
